import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useClientData } from '../ClientLayout.jsx';
import { useAuth } from '../../auth/AuthContext.jsx';
import { PIPELINE_STEPS, boardColumnIdForStatus } from '../../lib/matterPipeline.js';
import { isPaidStatus } from '../../lib/paidBeforeAssignment.js';
import { formatMattersMoneySum } from '../../lib/matterCurrency.js';
import { buildClientReports } from '../../lib/clientReports.js';
import {
  REPORT_LIST_COLUMNS,
  REPORT_METRIC_OPTIONS,
  deleteCustomReport,
  findReportById,
  listReportCatalog,
  loadCustomReports,
  loadReportsListSort,
  loadVisibleReportColumnIds,
  reportCellValue,
  reportSortFieldLabel,
  resolveReportMetricRows,
  exportReportCsv,
  exportReportPdf,
  reportRowsHaveDetail,
  saveReportsListSort,
  saveVisibleReportColumnIds,
  sortReports,
  upsertCustomReport,
  visibleReportColumns,
  defaultVisibleReportColumnIds,
} from '../../lib/reportsCatalog.js';
import { nextColumnSort, sortStatusLabel } from '../../lib/listColumnSort.js';
import ListPagination, { useListPagination } from '../ListPagination.jsx';
import ListSortMark from '../ListSortMark.jsx';

function money(list) {
  return formatMattersMoneySum(list, (m) => m.Amount_Owed);
}

function useReportListColumns() {
  const [visibleIds, setVisibleIds] = useState(() => loadVisibleReportColumnIds());

  function toggleColumn(id) {
    const col = REPORT_LIST_COLUMNS.find((c) => c.id === id);
    if (!col || col.required) return;
    setVisibleIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      return saveVisibleReportColumnIds(next);
    });
  }

  function resetColumns() {
    setVisibleIds(saveVisibleReportColumnIds(defaultVisibleReportColumnIds()));
  }

  return { visibleIds, toggleColumn, resetColumns };
}

function ReportColumnsPicker({ visibleIds, onToggle, onReset }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="list-toolbar-menu" ref={menuRef}>
      <button
        type="button"
        className="btn-ghost list-columns-btn"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((value) => !value)}
      >
        Filter
      </button>
      {open ? (
        <div className="list-columns-menu" role="dialog" aria-label="Filter by column">
          <div className="list-columns-menu-head">
            <strong>Filter by column</strong>
            <button type="button" className="text-link strong" onClick={onReset}>
              Reset
            </button>
          </div>
          <ul className="list-columns-options">
            {REPORT_LIST_COLUMNS.map((col) => {
              const checked = visibleIds.includes(col.id);
              return (
                <li key={col.id}>
                  <label className="list-columns-option">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={Boolean(col.required)}
                      onChange={() => onToggle(col.id)}
                    />
                    <span>
                      {col.label}
                      {col.required ? ' (required)' : ''}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function reportCell(value) {
  if (value == null || value === '') return '—';
  return value;
}

/** CSV / PDF download menu for report detail pages. */
function ReportDownloadMenu({ reportName, description = '', rows }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function downloadCsv() {
    setOpen(false);
    exportReportCsv(rows, { reportName });
  }

  function downloadPdf() {
    setOpen(false);
    void exportReportPdf(rows, { reportName, description });
  }

  return (
    <div className="list-bulk-menu report-download-menu" ref={menuRef}>
      <button
        type="button"
        className="btn-ghost list-bulk-toggle"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
      >
        Download
        <span aria-hidden>▾</span>
      </button>
      {open ? (
        <div
          className="list-bulk-dropdown report-download-dropdown"
          role="menu"
          aria-label="Download report"
        >
          <button type="button" role="menuitem" className="list-bulk-item" onClick={downloadCsv}>
            CSV
          </button>
          <button type="button" role="menuitem" className="list-bulk-item" onClick={downloadPdf}>
            PDF
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ReportMetricList({ rows, emptyLabel = 'No metrics in this report.' }) {
  if (!rows?.length) {
    return <div className="empty-state">{emptyLabel}</div>;
  }
  const showDetail = reportRowsHaveDetail(rows);
  return (
    <div className="debtor-table-wrap">
      <table className="matters-table debtor-table reports-ledger-table">
        <thead>
          <tr>
            <th>Metric</th>
            <th>Count</th>
            <th>Amount</th>
            {showDetail ? <th>Detail</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="matter-row">
              <td>
                <strong>{row.label}</strong>
              </td>
              <td>{reportCell(row.count)}</td>
              <td>{reportCell(row.amount)}</td>
              {showDetail ? (
                <td>{row.detail ? <span className="muted-line">{row.detail}</span> : '—'}</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportsTable({ reports, columns, onOpenReport, onBulkDelete }) {
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [sort, setSort] = useState(() => loadReportsListSort());
  const bulkMenuRef = useRef(null);
  const sorted = useMemo(() => sortReports(reports, sort), [reports, sort]);
  const pagination = useListPagination(sorted, { storageKey: 'reports' });
  const pageReports = pagination.pageItems;
  const sortLabel = sortStatusLabel(sort, reportSortFieldLabel);

  function toggleHeaderSort(columnId) {
    const col = REPORT_LIST_COLUMNS.find((c) => c.id === columnId);
    setSort(saveReportsListSort(nextColumnSort(sort, columnId, col?.sortType || 'string')));
  }

  useEffect(() => {
    setSelected(new Set());
    setBulkOpen(false);
  }, [reports, pagination.page, pagination.pageSize, sort.columnId, sort.direction]);

  useEffect(() => {
    if (!selected.size) setBulkOpen(false);
  }, [selected.size]);

  useEffect(() => {
    if (!bulkOpen) return undefined;
    function onDocClick(e) {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) {
        setBulkOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setBulkOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [bulkOpen]);

  function toggleOne(id, checked) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(String(id));
      else next.delete(String(id));
      return next;
    });
  }

  function toggleSelectAll(checked) {
    if (!checked) {
      setSelected(new Set());
      return;
    }
    setSelected(
      new Set(pageReports.filter((r) => r.type === 'custom').map((r) => String(r.id)))
    );
  }

  const pageCustomIds = pageReports.filter((r) => r.type === 'custom').map((r) => String(r.id));
  const selectedOnPage = pageCustomIds.filter((id) => selected.has(id)).length;
  const allSelected = pageCustomIds.length > 0 && selectedOnPage === pageCustomIds.length;
  const someSelected = selectedOnPage > 0 && selectedOnPage < pageCustomIds.length;
  const selectedCustom = [...selected].filter((id) =>
    reports.some((r) => r.id === id && r.type === 'custom')
  );

  function runBulkDelete() {
    setBulkOpen(false);
    onBulkDelete?.(selectedCustom);
    setSelected(new Set());
  }

  if (!reports.length) {
    return <div className="empty-state">No reports yet. Create a custom report to get started.</div>;
  }

  return (
    <div className="list-view debtor-list-view">
      <div className="list-view-toolbar">
        <p className="list-view-count">
          {reports.length} report{reports.length === 1 ? '' : 's'}
          {selectedCustom.length ? ` · ${selectedCustom.length} selected` : ''}
          {sortLabel ? ` · ${sortLabel}` : ''}
        </p>
        {selectedCustom.length ? (
          <div className="list-bulk-menu" ref={bulkMenuRef}>
            <button
              type="button"
              className="btn-ghost list-bulk-toggle"
              aria-expanded={bulkOpen}
              aria-haspopup="menu"
              onClick={() => setBulkOpen((open) => !open)}
            >
              Bulk Actions
              <span aria-hidden>▾</span>
            </button>
            {bulkOpen ? (
              <div className="list-bulk-dropdown" role="menu" aria-label="Bulk report actions">
                <button
                  type="button"
                  role="menuitem"
                  className="list-bulk-item is-danger"
                  onClick={runBulkDelete}
                >
                  Delete custom
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="debtor-table-wrap">
        <table className="matters-table debtor-table">
          <thead>
            <tr>
              <th className="list-col-check">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                  aria-label="Select all custom reports on this page"
                  disabled={!pageCustomIds.length}
                />
              </th>
              {columns.map((col) => {
                const active = sort.columnId === col.id;
                return (
                  <th key={col.id}>
                    <button
                      type="button"
                      className={`list-sort-header${active ? ' is-active' : ''}`}
                      onClick={() => toggleHeaderSort(col.id)}
                      aria-label={`Sort by ${col.label} ${
                        active && sort.direction === 'asc' ? 'descending' : 'ascending'
                      }`}
                    >
                      <span className="list-col-label">{col.label}</span>
                      <ListSortMark active={active} direction={sort.direction} />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageReports.map((report) => {
              const id = String(report.id);
              const isSelected = selected.has(id);
              const isCustom = report.type === 'custom';
              return (
                <tr
                  key={report.id}
                  className={`matter-row is-clickable${isSelected ? ' is-selected' : ''}`}
                  onClick={() => onOpenReport(report)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onOpenReport(report);
                    }
                  }}
                  tabIndex={0}
                  role="link"
                  aria-label={`Open report ${report.name}`}
                >
                  <td
                    className="list-col-check"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={!isCustom}
                      onChange={(e) => toggleOne(id, e.target.checked)}
                      aria-label={
                        isCustom
                          ? `Select ${report.name}`
                          : `${report.name} is built-in and cannot be selected`
                      }
                    />
                  </td>
                  {columns.map((col) => (
                    <td key={col.id}>
                      {col.id === 'name' ? (
                        <strong className="list-column-link">
                          {reportCellValue(report, col.id)}
                        </strong>
                      ) : (
                        reportCellValue(report, col.id)
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ListPagination
        page={pagination.page}
        pageCount={pagination.pageCount}
        pageSize={pagination.pageSize}
        onPageSizeChange={pagination.setPageSize}
        rangeStart={pagination.rangeStart}
        rangeEnd={pagination.rangeEnd}
        canPrev={pagination.canPrev}
        canNext={pagination.canNext}
        onPrev={pagination.goPrev}
        onNext={pagination.goNext}
      />
    </div>
  );
}

/** Reports catalog — ledger list of built-in + custom reports. */
export function ReportsOverviewPage() {
  const navigate = useNavigate();
  const [customReports, setCustomReports] = useState(() => loadCustomReports());
  const [notice, setNotice] = useState('');
  const { visibleIds, toggleColumn, resetColumns } = useReportListColumns();
  const columns = useMemo(() => visibleReportColumns(visibleIds), [visibleIds]);
  const reports = useMemo(() => listReportCatalog(customReports), [customReports]);

  function handleBulkDelete(ids) {
    if (!ids?.length) return;
    const ok = window.confirm(
      ids.length === 1
        ? 'Delete this custom report? Built-in reports are not affected.'
        : `Delete ${ids.length} custom reports? Built-in reports are not affected.`
    );
    if (!ok) return;
    let next = customReports;
    for (const id of ids) {
      next = deleteCustomReport(id);
    }
    setCustomReports(next);
    setNotice(
      `Deleted ${ids.length} custom report${ids.length === 1 ? '' : 's'}.`
    );
  }

  return (
    <div className="client-page">
      <div className="section-head">
        <div>
          <h1>Reports</h1>
          <p>
            Open a row to view totals, or create your own report from the metrics you care about.
          </p>
        </div>
        <div className="section-head-actions">
          <Link className="btn-primary" to="/client/reports/new">
            New report
          </Link>
          <ReportColumnsPicker
            visibleIds={visibleIds}
            onToggle={toggleColumn}
            onReset={resetColumns}
          />
        </div>
      </div>

      {notice ? <p className="inline-notice">{notice}</p> : null}

      <ReportsTable
        reports={reports}
        columns={columns}
        onOpenReport={(report) => navigate(`/client/reports/${report.id}`)}
        onBulkDelete={handleBulkDelete}
      />
    </div>
  );
}

/** Create / edit a custom report. */
export function ReportEditorPage() {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const editing = reportId && reportId !== 'new' ? findReportById(reportId) : null;
  const isEdit = Boolean(editing && editing.type === 'custom');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [metricIds, setMetricIds] = useState(() => ['collected.week', 'volume.open']);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      setName(editing.name || '');
      setDescription(editing.description || '');
      setMetricIds([...(editing.metricIds || [])]);
      setError('');
      return;
    }
    setName('');
    setDescription('');
    setMetricIds(['collected.week', 'volume.open']);
    setError('');
  }, [isEdit, editing?.id]);

  if (reportId && reportId !== 'new' && (!editing || editing.type !== 'custom')) {
    return <Navigate to="/client/reports" replace />;
  }

  function toggleMetric(id) {
    setMetricIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function onSubmit(e) {
    e.preventDefault();
    setError('');
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give this report a name.');
      return;
    }
    if (!metricIds.length) {
      setError('Pick at least one metric.');
      return;
    }
    const { report } = upsertCustomReport({
      id: isEdit ? editing.id : undefined,
      name: trimmed,
      description: description.trim(),
      metricIds,
    });
    if (!report) {
      setError('Could not save this report.');
      return;
    }
    navigate(`/client/reports/${report.id}`, { replace: true });
  }

  const groups = useMemo(() => {
    const map = new Map();
    for (const opt of REPORT_METRIC_OPTIONS) {
      if (!map.has(opt.group)) map.set(opt.group, []);
      map.get(opt.group).push(opt);
    }
    return [...map.entries()];
  }, []);

  return (
    <div className="client-page">
      <div className="section-head">
        <div>
          <h1>{isEdit ? 'Edit report' : 'New report'}</h1>
          <p>Choose a name and the metrics this report should include.</p>
        </div>
        <div className="section-head-actions">
          <Link className="btn-ghost" to="/client/reports">
            Back to reports
          </Link>
        </div>
      </div>

      <form className="reports-editor-form" onSubmit={onSubmit}>
        {error ? <p className="form-error">{error}</p> : null}
        <label className="field">
          <span>Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Weekly pulse"
            autoFocus
          />
        </label>
        <label className="field">
          <span>Description (optional)</span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short note shown in the reports list"
          />
        </label>

        <fieldset className="reports-metric-fieldset">
          <legend>Metrics</legend>
          {groups.map(([group, opts]) => (
            <div key={group} className="reports-metric-group">
              <h3>{group}</h3>
              <ul className="list-columns-options">
                {opts.map((opt) => (
                  <li key={opt.id}>
                    <label className="list-columns-option">
                      <input
                        type="checkbox"
                        checked={metricIds.includes(opt.id)}
                        onChange={() => toggleMetric(opt.id)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </fieldset>

        <div className="reports-editor-actions">
          <button type="submit" className="btn-primary">
            {isEdit ? 'Save report' : 'Create report'}
          </button>
          <Link className="btn-ghost" to="/client/reports">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

/** View a built-in or custom report as a ledger of metric rows. */
export function ReportDetailPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { allMatters, debtors } = useClientData();
  const report = findReportById(reportId);
  const viewer = useMemo(
    () => ({ email: user?.email || '', name: user?.name || user?.displayName || '' }),
    [user?.email, user?.name, user?.displayName]
  );

  const snapshot = useMemo(
    () =>
      buildClientReports({
        allMatters,
        debtors,
        now: new Date(),
        viewer,
      }),
    [allMatters, debtors, viewer]
  );

  if (!report) {
    return <Navigate to="/client/reports" replace />;
  }

  // Preserve specialized layouts for classic built-ins.
  if (report.id === 'pipeline') {
    return <ReportsPipelinePage embedded />;
  }
  if (report.id === 'collections') {
    return <ReportsCollectionsPage embedded />;
  }

  const rows = resolveReportMetricRows(report, snapshot);

  function handleDelete() {
    if (report.type !== 'custom') return;
    const ok = window.confirm(`Delete custom report “${report.name}”?`);
    if (!ok) return;
    deleteCustomReport(report.id);
    navigate('/client/reports', { replace: true });
  }

  return (
    <div className="client-page">
      <div className="section-head">
        <div>
          <h1>{report.name}</h1>
          <p>{report.description || 'Report metrics for your portfolio.'}</p>
        </div>
        <div className="section-head-actions">
          <Link className="btn-ghost" to="/client/reports">
            All reports
          </Link>
          <ReportDownloadMenu
            reportName={report.name}
            description={report.description || ''}
            rows={rows}
          />
          {report.type === 'custom' ? (
            <>
              <Link className="btn-ghost" to={`/client/reports/${report.id}/edit`}>
                Edit
              </Link>
              <button type="button" className="btn-ghost" onClick={handleDelete}>
                Delete
              </button>
            </>
          ) : null}
        </div>
      </div>

      <ReportMetricList rows={rows} />
    </div>
  );
}

export function ReportsPipelinePage({ embedded = false }) {
  const { matters } = useClientData();
  const rows = PIPELINE_STEPS.map((step) => {
    const list = matters.filter((m) => boardColumnIdForStatus(m.Status) === step.id);
    return { ...step, count: list.length, list };
  });
  const exportRows = rows.map((r) => ({
    label: r.label,
    count: r.count,
    amount: money(r.list),
    detail: null,
  }));

  return (
    <div className="client-page">
      <div className="section-head">
        <div>
          <h1>Pipeline</h1>
          <p>Counts and dollars by demand status (active demands).</p>
        </div>
        <div className="section-head-actions">
          <Link className="btn-ghost" to="/client/reports">
            All reports
          </Link>
          <ReportDownloadMenu
            reportName="Pipeline"
            description="Counts and dollars by demand status (active demands)."
            rows={exportRows}
          />
        </div>
      </div>

      <div className="debtor-table-wrap">
        <table className="matters-table debtor-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Demands</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="matter-row">
                <td>
                  <strong>{r.label}</strong>
                </td>
                <td>{r.count}</td>
                <td>{money(r.list)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ReportsCollectionsPage({ embedded = false }) {
  const { matters } = useClientData();
  const paid = matters.filter((m) => isPaidStatus(m.Status));
  const open = matters.filter((m) => !isPaidStatus(m.Status));
  const exportRows = [
    {
      label: 'Recovered',
      count: paid.length,
      amount: money(paid),
      detail: 'Paid demands',
    },
    {
      label: 'Outstanding',
      count: open.length,
      amount: money(open),
      detail: 'Open demands',
    },
  ];

  return (
    <div className="client-page">
      <div className="section-head">
        <div>
          <h1>Collections</h1>
          <p>Recovered vs outstanding balances on active demands.</p>
        </div>
        <div className="section-head-actions">
          <Link className="btn-ghost" to="/client/reports">
            All reports
          </Link>
          <ReportDownloadMenu
            reportName="Collections"
            description="Recovered vs outstanding balances on active demands."
            rows={exportRows}
          />
        </div>
      </div>

      <div className="debtor-table-wrap">
        <table className="matters-table debtor-table reports-ledger-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Count</th>
              <th>Amount</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            <tr className="matter-row">
              <td>
                <strong>Recovered</strong>
              </td>
              <td>{paid.length}</td>
              <td>{money(paid)}</td>
              <td>
                <span className="muted-line">Paid demands</span>
              </td>
            </tr>
            <tr className="matter-row">
              <td>
                <strong>Outstanding</strong>
              </td>
              <td>{open.length}</td>
              <td>{money(open)}</td>
              <td>
                <span className="muted-line">Open demands</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
